// filepath: apps/web/src/features/directory/person-summary/PersonSummaryPage.tsx
//
// FR-UI-DIR-1 AC-2 — person summary page (5 tabs).
//
// Tabs:
//   1. Overview       -- document header + key facts panel.
//   2. Contacts       -- server-emitted contact points; no
//                         reconstruction of email/phone from related
//                         records (INV-2).
//   3. Employment     -- employment assignments feed (read-only).
//   4. Qualifications -- server-supplied qualifications list. If the
//                         per-person qualifications endpoint is
//                         unavailable (F1: teams/qualifications
//                         deferred), the page renders the
//                         qualifications-not-shipped state and never
//                         calls the missing endpoint.
//   5. Audit          -- audit feed entries for the person.
//
// INV-2: no field-reconstruction helpers. Only server-emitted fields
// are rendered.
// INV-9: zero dangerouslySetInnerHTML.

import { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Tabs } from "@/design-system";
import {
  usePersonSummary,
  usePersonEmployment,
  usePersonQualifications,
  usePersonAudit,
} from "../queries";
import { PersonSummaryHeader } from "./PersonSummaryHeader";
import type { PersonSummary } from "../types";

export interface PersonSummaryPageProps {
  personId: string;
}

type TabKey =
  "overview" | "contacts" | "employment" | "qualifications" | "audit";

const TAB_KEYS: readonly TabKey[] = [
  "overview",
  "contacts",
  "employment",
  "qualifications",
  "audit",
];

export function PersonSummaryPage({
  personId,
}: PersonSummaryPageProps): React.ReactElement {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabKey>("overview");

  const summary = usePersonSummary(personId);

  switch (summary.state) {
    case "loading":
      return (
        <p
          className="directory-person__loading"
          data-testid="person-summary-loading"
        >
          {t("directory.person.summary.state.loading")}
        </p>
      );
    case "permission-denied":
      return (
        <p
          className="directory-person__permission-denied"
          role="alert"
          data-testid="person-summary-permission-denied"
        >
          {t("directory.person.summary.state.permission_denied")}
        </p>
      );
    case "validation-error":
      return (
        <p
          className="directory-person__validation-error"
          role="alert"
          data-testid="person-summary-validation-error"
        >
          {summary.error?.message ??
            t("directory.person.summary.state.validation_error")}
        </p>
      );
    case "fetch-failure":
      return (
        <p
          className="directory-person__fetch-failure"
          role="alert"
          data-testid="person-summary-fetch-failure"
        >
          {t("directory.person.summary.state.fetch_failure")}
        </p>
      );
    case "rate-limit":
      return (
        <p
          className="directory-person__rate-limit"
          role="alert"
          data-testid="person-summary-rate-limit"
        >
          {t("directory.person.summary.state.rate_limit")}
        </p>
      );
    case "success": {
      const person = summary.data as PersonSummary;
      return (
        <section
          className="directory-person"
          data-testid="person-summary-page"
          data-person-id={personId}
        >
          <PersonSummaryHeader person={person} />
          <Tabs
            data-testid="person-summary-tabs"
            activeId={tab}
            onChange={(next) => setTab(next as TabKey)}
            tabs={TAB_KEYS.map((key) => ({
              id: key,
              label: t(
                `directory.person.summary.tab.${key}`,
                defaultTabLabel(key),
              ),
            }))}
          />
          <div
            className="directory-person__panel"
            data-testid={`person-summary-tab-${tab}`}
          >
            {renderTab(tab, personId, person, t)}
          </div>
        </section>
      );
    }
  }
}

function defaultTabLabel(key: TabKey): string {
  switch (key) {
    case "overview":
      return "Overview";
    case "contacts":
      return "Contacts";
    case "employment":
      return "Employment";
    case "qualifications":
      return "Qualifications";
    case "audit":
      return "Audit";
  }
}

function renderTab(
  tab: TabKey,
  personId: string,
  _person: PersonSummary,
  t: ReturnType<typeof useTranslation>["t"],
): React.ReactElement {
  switch (tab) {
    case "overview":
      return (
        <p data-testid="person-summary-overview">
          {t("directory.person.summary.overview.body")}
        </p>
      );
    case "contacts":
      return (
        <PersonContactsPanel personId={personId} key={`contacts-${personId}`} />
      );
    case "employment":
      return (
        <PersonEmploymentPanel
          personId={personId}
          key={`employment-${personId}`}
        />
      );
    case "qualifications":
      return (
        <PersonQualificationsPanel
          personId={personId}
          key={`qualifications-${personId}`}
        />
      );
    case "audit":
      return <PersonAuditPanel personId={personId} key={`audit-${personId}`} />;
  }
}

function PersonContactsPanel({
  personId,
}: {
  personId: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const query = usePersonSummary(personId);
  if (query.state !== "success" || query.data === null) {
    return (
      <p data-testid="person-summary-contacts-pending">
        {t("directory.person.summary.contacts.pending")}
      </p>
    );
  }
  const contacts = query.data.contacts ?? [];
  if (contacts.length === 0) {
    return (
      <p data-testid="person-summary-contacts-empty">
        {t("directory.person.summary.contacts.empty")}
      </p>
    );
  }
  return (
    <ul data-testid="person-summary-contacts-list">
      {contacts.map((c) => (
        <li key={c.id}>
          <span>{c.kind}</span>: <span>{c.value}</span>
        </li>
      ))}
    </ul>
  );
}

function PersonEmploymentPanel({
  personId,
}: {
  personId: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const query = usePersonEmployment(personId);
  switch (query.state) {
    case "loading":
      return (
        <p data-testid="person-summary-employment-loading">
          {t("directory.person.summary.employment.loading")}
        </p>
      );
    case "fetch-failure":
      return (
        <p role="alert" data-testid="person-summary-employment-fetch-failure">
          {t("directory.person.summary.employment.fetch_failure")}
        </p>
      );
    case "success": {
      const data = query.data;
      if (data === null) {
        return (
          <p role="alert" data-testid="person-summary-employment-fetch-failure">
            {t("directory.person.summary.employment.fetch_failure")}
          </p>
        );
      }
      const entries = data.items ?? [];
      if (entries.length === 0) {
        return (
          <p data-testid="person-summary-employment-empty">
            {t("directory.person.summary.employment.empty")}
          </p>
        );
      }
      return (
        <ul data-testid="person-summary-employment-list">
          {entries.map((e) => (
            <li key={e.id}>
              {e.position_title ?? e.position_id}
              {e.unit_name ? ` @ ${e.unit_name}` : ""}
              {e.start_date ? ` (${e.start_date}…)` : ""}
            </li>
          ))}
        </ul>
      );
    }
    default:
      return (
        <p role="alert" data-testid="person-summary-employment-error">
          {t("directory.person.summary.employment.unavailable")}
        </p>
      );
  }
}

function PersonQualificationsPanel({
  personId,
}: {
  personId: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const query = usePersonQualifications(personId);
  // F1: the per-person qualifications endpoint is deferred. The hook
  // is wired to the deferred factory and immediately resolves to a
  // qualifications-not-shipped state. We MUST NOT call the not-yet-
  // shipped endpoint (static-guard test enforces).
  if (query.state === "fetch-failure") {
    return (
      <div role="alert" data-testid="person-summary-qualifications-deferred">
        <p>{t("directory.qualifications.deferred.title")}</p>
        <p>{t("directory.qualifications.deferred.body")}</p>
      </div>
    );
  }
  return (
    <p data-testid="person-summary-qualifications-pending">
      {t("directory.qualifications.pending")}
    </p>
  );
}

function PersonAuditPanel({
  personId,
}: {
  personId: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const query = usePersonAudit(personId);
  if (query.state !== "success" || query.data === null) {
    return (
      <p data-testid="person-summary-audit-pending">
        {t("directory.audit.pending")}
      </p>
    );
  }
  const entries = query.data.items ?? [];
  return (
    <ul data-testid="person-summary-audit-list">
      {entries.map((e) => (
        <li key={e.id}>
          {e.timestamp ?? e.created_at} — {e.actor_id ?? e.author_identity} —{" "}
          {e.action}
        </li>
      ))}
    </ul>
  );
}
